import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import SelectBox from '../../shared/component/SelectBox';
import { PDFAssetData } from '../../shared/dto/EntityRepository';
import { AlertPosition, AlertType, DisplayAlignment, SelectType, TextAlignment } from '../../shared/enum/EnumRepository';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import { PEHeader, PEImgContentWrapper, PEIndicator, SelectBoxContainer, SelectBoxWrapper } from './Work';
import { convertS3UrlToCloudFrontUrl } from '../../shared/aws/awsHelper';
import PdfIcon from '../../asset/icon/pdf.png';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { openNewTab } from '../../shared/util/openNewTab';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import { InputPEPdfFileName } from '../../shared/component/headless/input/InputBody';
import { PDF_DESCRIPTION_MIN_LENGTH, PDF_DESCRIPTION_MAX_LENGTH, PDF_DESCRIPTION_MAX_DISPLAY_LENGTH_DESKTOP } from '../../shared/constants/limits';
import { removeFileExtensionWithinLimit } from '../../shared/util/imageUtils';
import { shrinkString } from '../../shared/util/shrinkString';

interface PdfAssetProps {
  peId: string;
  alignment: DisplayAlignment;
  data: PDFAssetData;
  priority: boolean;
}

const PdfAsset: React.FC<PdfAssetProps> = ({ peId, alignment, data }) => {
  const { isEditMode } = useEditMode();
  const updateDisplayAlignment = useProjectElementListStore((s) => s.updateDisplayAlignment);
  const updatePDF = useProjectElementListStore((s) => s.updatePDF);
  const updatePDFFile = useProjectElementListStore((s) => s.updatePDFFile);
  const { enqueueAlert } = useStandardAlertStore();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const convertedCloudFrontUrl = convertS3UrlToCloudFrontUrl(data.uploadFile.originUrl);
  const convertedFileSize = data.fileSize ? `${(data.fileSize / 1024 / 1024).toFixed(2)} MB` : '0 MB';
  const pdfDescription = isEditMode ? data.description : shrinkString(data.description, PDF_DESCRIPTION_MAX_DISPLAY_LENGTH_DESKTOP);

  const handleOpenPDF = () => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_MOVE_TO_PDF'),
      callBack: () => openNewTab(convertedCloudFrontUrl),
    });
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updatePDF(peId, { description: e.target.value });
  }

  const changePDFFile = () => {
    if (!isEditMode) return;
    fileInputRef.current?.click();
  }

  const handlePDFFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) return;

    if (data.description === '') {
      updatePDF(peId, { description: removeFileExtensionWithinLimit(file.name, PDF_DESCRIPTION_MAX_LENGTH) });
    }

    const reader = new FileReader();
    reader.onload = () => {
      updatePDFFile(peId, reader.result as string, file.size);
    };
    reader.readAsDataURL(file);
  }

  return (
    <PEImgContentWrapper>
      {isEditMode &&
        <PEHeader>
          <SelectBoxContainer>
            <SelectBoxWrapper>
              <SelectBox
                value={alignment || DisplayAlignment.CENTER}
                selectType={SelectType.DISPLAY_ALIGNMENT}
                handleChange={(value) => updateDisplayAlignment(peId, value)}
                direction={false} />
            </SelectBoxWrapper>
          </SelectBoxContainer>
          <PEIndicator peId={peId}>: PDF</PEIndicator>
        </PEHeader>
      }
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        onChange={handlePDFFileChange}
      />
      <PDFFrameWrapper $alignment={alignment || DisplayAlignment.CENTER}>
        <PDFLink onClick={isEditMode ? undefined : handleOpenPDF} $isClickable={!isEditMode}>
          <PDFIcon src={PdfIcon} alt={`Open ${data.description || 'PDF'}`} onClick={changePDFFile} />
          <PDFNameWrapper>
          <MoleculeInputDiv
            value={pdfDescription}
            placeholder={"Add name for PDF file."}
            defaultValue={"Open PDF"}
            DefaultDiv={PDFName}
            handleChange={handleChange}
            inputStyle={InputPEPdfFileName}
            StyledDiv={PDFName}
            alignment={TextAlignment.CENTER}
            minLimit={PDF_DESCRIPTION_MIN_LENGTH}
            maxLimit={PDF_DESCRIPTION_MAX_LENGTH}
          />
          <PDFExtension>.pdf</PDFExtension>
          </PDFNameWrapper>
          {isEditMode && <PDFSize>({convertedFileSize})</PDFSize>}
        </PDFLink>
      </PDFFrameWrapper>
    </PEImgContentWrapper>
  );
};

const PDFFrameWrapper = styled.div<{ $alignment: DisplayAlignment }>`
  width: 100%;
  padding: 0 calc(8vw);
  display: flex;
  flex-direction: column;
  align-items: ${({ $alignment }) => {
    switch ($alignment) {
      case DisplayAlignment.LEFT_FLUSH:
      case DisplayAlignment.LEFT_OFFSET:
        return 'flex-start';
      case DisplayAlignment.RIGHT_FLUSH:
      case DisplayAlignment.RIGHT_OFFSET:
        return 'flex-end';
      case DisplayAlignment.CENTER:
      default:
        return 'center';
    }
  }};
`;

const PDFIcon = styled.img`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const PDFLink = styled.div<{ $isClickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
  cursor: ${({ $isClickable }) => $isClickable ? 'pointer' : 'default'};
`;

const PDFNameWrapper = styled.div`
  display: flex;
`;
const PDFName = styled.div`
  font-style: italic;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const PDFExtension = styled.span`
  padding-left: 2px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const PDFSize = styled.span`
`;

const HiddenFileInput = styled.input`
  display: none;
`;

export default PdfAsset;
